import React, { useEffect, useMemo, useState } from "react";
import "./position.css";

/** ROJOS en ruleta europea */
var RED_NUMBERS = {};
[
  1, 3, 5, 7, 9, 12, 14, 16, 18,
  19, 21, 23, 25, 27, 30, 32, 34, 36,
].forEach(function (n) { RED_NUMBERS[n] = true; });

function numColor(n) {
  if (n === 0) return "green";
  return RED_NUMBERS[n] ? "red" : "black";
}

function formatMoney(n) {
  if (typeof n !== "number") return "—";
  try {
    return new Intl.NumberFormat("es-DO", { maximumFractionDigits: 0 }).format(n);
  } catch (e) {
    return String(n);
  }
}

function buildRows() {
  // EXACTO estilo bwin:
  // fila 1: 3,6,9...36
  // fila 2: 2,5,8...35
  // fila 3: 1,4,7...34
  var top = [], mid = [], bot = [];
  for (var i = 3; i <= 36; i += 3) top.push(i);
  for (var j = 2; j <= 35; j += 3) mid.push(j);
  for (var k = 1; k <= 34; k += 3) bot.push(k);
  return { top: top, mid: mid, bot: bot };
}

function keyN(n) { return "N:" + n; } // solo números

/**
 * PROPS IMPORTANTES:
 * - credit: number (crédito disponible)
 * - maxBetPerNumber: number (máximo permitido por número)
 * - maxTotalBet: number (máximo permitido total)
 * - chipOptions: number[]
 * - backgroundImageUrl: string (imagen de fondo estilo “mesa”)
 * - onPlaceBet: async ({ bets, total, chipValue, lastMemorizedStake }) => ({ticketId} | void)
 * - lastWinningNumber, jackpot, etc (opcionales)
 */
export default function RoulettePOS36(props) {
  var chipOptions = (props && props.chipOptions) ? props.chipOptions : [20, 50, 100, 200, 500, 1000];
  var initialChipValue = (props && typeof props.initialChipValue === "number") ? props.initialChipValue : chipOptions[0];

  var credit = (props && typeof props.credit === "number") ? props.credit : 1000;
  var maxBetPerNumber = (props && typeof props.maxBetPerNumber === "number") ? props.maxBetPerNumber : 5000;
  var maxTotalBet = (props && typeof props.maxTotalBet === "number") ? props.maxTotalBet : 20000;

  var lastWinningNumber =
    (props && typeof props.lastWinningNumber === "number") ? props.lastWinningNumber :
    (props && props.lastWinningNumber === 0) ? 0 : null;

  var jackpot = (props && props.jackpot) ? props.jackpot : { value: 0, lastWinnerTicket: "—" };
  var onPlaceBet = (props && props.onPlaceBet) ? props.onPlaceBet : null;

  // imagen modelo (puedes cambiarla por prop)
  var backgroundImageUrl = (props && props.backgroundImageUrl)
    ? props.backgroundImageUrl
    : "https://www.bwin.es/es/games/blog/cdn/img/1aa35626-8354-4c67-9012-aff180d63668.jpeg?w=1536&h=440&fm=jpeg&q=80";

  var [chipValue, setChipValue] = useState(initialChipValue);

  // bets guarda SOLO números: { "N:7": 200, "N:10": 50 ... }
  var [bets, setBets] = useState({});
  var [locked, setLocked] = useState(false);
  var [isPlacing, setIsPlacing] = useState(false);
  var [lastTicketId, setLastTicketId] = useState("");
  var [msg, setMsg] = useState(""); // mensajes rápidos

  // ✅ Memoria de la última apuesta (monto final de la última celda tocada)
  var [lastMemorizedStake, setLastMemorizedStake] = useState(0);

  // ✅ orientación
  var [isPortrait, setIsPortrait] = useState(false);

  var rows = useMemo(function () { return buildRows(); }, []);

  useEffect(function () {
    function check() {
      // matchMedia orientation es más fiable en móviles
      var portrait = false;
      try {
        portrait = window.matchMedia && window.matchMedia("(orientation: portrait)").matches;
      } catch (e) {
        portrait = window.innerHeight > window.innerWidth;
      }
      setIsPortrait(!!portrait);
    }

    check();
    window.addEventListener("resize", check);
    window.addEventListener("orientationchange", check);
    return function () {
      window.removeEventListener("resize", check);
      window.removeEventListener("orientationchange", check);
    };
  }, []);

  var total = useMemo(function () {
    var sum = 0;
    Object.keys(bets).forEach(function (k) { sum += Number(bets[k]) || 0; });
    return sum;
  }, [bets]);

  function showMsg(text) {
    setMsg(text);
    // auto-hide
    window.clearTimeout(showMsg._t);
    showMsg._t = window.setTimeout(function () { setMsg(""); }, 1800);
  }

  function updateBets(next) {
    setBets(next);
  }

  function canAddAmount(addAmount, currentNumberAmount) {
    // Validación por número
    if ((currentNumberAmount + addAmount) > maxBetPerNumber) {
      return { ok: false, reason: "Máximo por número: " + formatMoney(maxBetPerNumber) };
    }
    // Validación total
    if ((total + addAmount) > maxTotalBet) {
      return { ok: false, reason: "Máximo total: " + formatMoney(maxTotalBet) };
    }
    // Validación crédito
    if ((total + addAmount) > credit) {
      return { ok: false, reason: "Crédito insuficiente" };
    }
    return { ok: true };
  }

  /**
   * ✅ Lógica pedida:
   * - Click en ficha -> queda activa
   * - Click en número:
   *   - si ya tenía apuesta: incrementa por chipValue
   *   - si NO tenía apuesta: asigna el "lastMemorizedStake" (si existe), si no chipValue
   * - Cada toque actualiza lastMemorizedStake con el monto final de esa casilla
   */
  function tapNumber(n) {
    if (locked) return;

    var k = keyN(n);
    var current = Number(bets[k]) || 0;

    var addAmount = 0;

    if (current > 0) {
      addAmount = chipValue; // incrementa por ficha seleccionada
    } else {
      // nueva apuesta: usa el monto memorizado si existe, si no usa chipValue
      addAmount = (lastMemorizedStake > 0) ? lastMemorizedStake : chipValue;
    }

    var check = canAddAmount(addAmount, current);
    if (!check.ok) {
      showMsg(check.reason);
      return;
    }

    var next = {};
    Object.keys(bets).forEach(function (kk) { next[kk] = bets[kk]; });

    var newAmount = current + addAmount;
    next[k] = newAmount;

    updateBets(next);

    // ✅ memorizar último monto final (para aplicarlo al siguiente número nuevo)
    setLastMemorizedStake(newAmount);
  }

  function removeNumberBet(n) {
    if (locked) return;
    var k = keyN(n);
    var next = {};
    Object.keys(bets).forEach(function (kk) { next[kk] = bets[kk]; });
    delete next[k];
    updateBets(next);
  }

  function clearBets() {
    setLocked(false);
    setLastTicketId("");
    updateBets({});
    showMsg("Apuesta borrada");
  }

  async function placeBet() {
    var keys = Object.keys(bets);
    if (!keys.length) {
      showMsg("Selecciona al menos un número");
      return;
    }
    if (!onPlaceBet) {
      showMsg("Falta conectar onPlaceBet");
      return;
    }

    try {
      setIsPlacing(true);
      var res = await onPlaceBet({
        bets: bets,
        total: total,
        chipValue: chipValue,
        lastMemorizedStake: lastMemorizedStake,
        locked: locked,
      });
      if (res && res.ticketId) setLastTicketId(String(res.ticketId));
      showMsg("Apuesta colocada");
      // ✅ No se limpia: se mantiene hasta borrarla
    } catch (e) {
      showMsg("Error al colocar apuesta");
    } finally {
      setIsPlacing(false);
    }
  }

  var lastColor = (lastWinningNumber === null || typeof lastWinningNumber !== "number")
    ? "black"
    : numColor(lastWinningNumber);

  // Ordenar resumen por número
  var summary = useMemo(function () {
    var arr = [];
    Object.keys(bets).forEach(function (k) {
      if (k.indexOf("N:") === 0) {
        var n = Number(k.split(":")[1]);
        var amt = Number(bets[k]) || 0;
        if (amt > 0) arr.push({ n: n, amount: amt });
      }
    });
    arr.sort(function (a, b) { return a.n - b.n; });
    return arr;
  }, [bets]);

  return (
    <div className="rpos2-root">
      {/* ✅ Overlay si está vertical */}
      {isPortrait ? (
        <div className="rpos2-rotate">
          <div className="rpos2-rotate-card">
            <div className="rpos2-rotate-icon">↻</div>
            <div className="rpos2-rotate-title">Coloca el dispositivo en horizontal</div>
            <div className="rpos2-rotate-sub">
              Para usar el Punto de Venta, gira la pantalla a modo paisaje.
            </div>
            <div
              className="rpos2-rotate-preview"
              style={{ backgroundImage: "url(" + backgroundImageUrl + ")" }}
            />
          </div>
        </div>
      ) : null}

      {/* Contenido solo si NO está vertical */}
      {!isPortrait ? (
        <div className="rpos2-wrap">
          {/* Barra info */}
          <div className="rpos2-topbar">
            <div className="rpos2-brand">
              <div className="rpos2-title">RULETA 0–36 • POS</div>
              <div className="rpos2-sub">
                Crédito: <b>{formatMoney(credit)}</b> • Máx/Num: <b>{formatMoney(maxBetPerNumber)}</b> • Máx/Total: <b>{formatMoney(maxTotalBet)}</b>
              </div>
            </div>

            <div className="rpos2-info">
              <div className="rpos2-kv">
                <span className="rpos2-k">Último</span>
                <span className={"rpos2-last rpos2-last-" + lastColor}>
                  {lastWinningNumber === null ? "—" : lastWinningNumber}
                </span>
              </div>
              <div className="rpos2-kv">
                <span className="rpos2-k">Jackpot</span>
                <span className="rpos2-pill rpos2-pill-yellow">{formatMoney(jackpot.value)}</span>
              </div>
              <div className="rpos2-kv">
                <span className="rpos2-k">Ticket JP</span>
                <span className="rpos2-pill rpos2-pill-purple">{jackpot.lastWinnerTicket || "—"}</span>
              </div>
              <div className="rpos2-kv">
                <span className="rpos2-k">Total</span>
                <span className="rpos2-pill rpos2-pill-cyan">{formatMoney(total)}</span>
              </div>
            </div>
          </div>

          {/* Mensaje rápido */}
          {msg ? <div className="rpos2-msg">{msg}</div> : null}

          <div className="rpos2-main">
            {/* Mesa */}
            <div className="rpos2-table" style={{ backgroundImage: "url(" + backgroundImageUrl + ")" }}>
              <div className="rpos2-table-overlay">
                {/* 0 + números + 2 a 1 */}
                <div className="rpos2-grid14">
                  {/* 0 vertical */}
                  <NumberCell
                    label="0"
                    amount={bets[keyN(0)] || 0}
                    tone="green"
                    tall={true}
                    locked={locked}
                    onClick={function () { tapNumber(0); }}
                  />

                  {/* fila top 3,6..36 */}
                  {rows.top.map(function (n) {
                    return (
                      <NumberCell
                        key={"t-" + n}
                        label={String(n)}
                        amount={bets[keyN(n)] || 0}
                        tone={numColor(n)}
                        locked={locked}
                        onClick={function () { tapNumber(n); }}
                      />
                    );
                  })}
                  {/* 2 a 1 top */}
                  <SideCell label="2 a 1" />

                  {/* fila mid 2,5..35 */}
                  {rows.mid.map(function (n) {
                    return (
                      <NumberCell
                        key={"m-" + n}
                        label={String(n)}
                        amount={bets[keyN(n)] || 0}
                        tone={numColor(n)}
                        locked={locked}
                        onClick={function () { tapNumber(n); }}
                      />
                    );
                  })}
                  {/* 2 a 1 mid */}
                  <SideCell label="2 a 1" />

                  {/* fila bot 1,4..34 */}
                  {rows.bot.map(function (n) {
                    return (
                      <NumberCell
                        key={"b-" + n}
                        label={String(n)}
                        amount={bets[keyN(n)] || 0}
                        tone={numColor(n)}
                        locked={locked}
                        onClick={function () { tapNumber(n); }}
                      />
                    );
                  })}
                  {/* 2 a 1 bot */}
                  <SideCell label="2 a 1" />
                </div>

                {/* Controles (chips + botones) */}
                <div className="rpos2-controls">
                  <div className="rpos2-chips">
                    {chipOptions.map(function (v, idx) {
                      var active = v === chipValue;
                      return (
                        <button
                          key={v}
                          type="button"
                          className={"chip " + "chip-" + (idx % 6) + (active ? " active" : "")}
                          onClick={function () { setChipValue(v); }}
                          disabled={locked}
                          title={"Ficha " + v}
                        >
                          <span className="chip-inner">{v}</span>
                        </button>
                      );
                    })}
                    <div className="rpos2-memo">
                      Memorizado: <b>{lastMemorizedStake > 0 ? formatMoney(lastMemorizedStake) : "—"}</b>
                    </div>
                  </div>

                  <div className="rpos2-actions">
                    <button className="btn btn-success" onClick={placeBet} disabled={isPlacing}>
                      {isPlacing ? "Colocando..." : "Colocar apuesta"}
                    </button>

                    <button
                      className={"btn " + (locked ? "btn-warning" : "btn-outline-primary")}
                      onClick={function () { setLocked(!locked); }}
                    >
                      {locked ? "Desbloquear" : "Mantener"}
                    </button>

                    <button className="btn btn-outline-danger" onClick={clearBets}>
                      Borrar
                    </button>

                    {lastTicketId ? (
                      <span className="rpos2-ticket">Ticket: <b>{lastTicketId}</b></span>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>

            {/* Resumen */}
            <div className="rpos2-summary">
              <div className="rpos2-summary-head">
                <span className="rpos2-summary-title">Resumen</span>
                <span className="rpos2-pill rpos2-pill-cyan">Total: {formatMoney(total)}</span>
              </div>

              <div className="rpos2-summary-list">
                {!summary.length ? (
                  <div className="rpos2-empty">No hay apuestas.</div>
                ) : (
                  summary.map(function (x) {
                    return (
                      <div key={x.n} className="rpos2-row">
                        <span className={"rpos2-badge badge-" + numColor(x.n)}>{x.n}</span>
                        <span className="rpos2-row-amt">{formatMoney(x.amount)}</span>
                        {!locked ? (
                          <button
                            className="btn btn-sm btn-link text-danger"
                            onClick={function () { removeNumberBet(x.n); }}
                          >
                            quitar
                          </button>
                        ) : null}
                      </div>
                    );
                  })
                )}
              </div>

              <div className="rpos2-footnote">
                ✅ Nueva apuesta en número nuevo usa el monto <b>memorizado</b>. En número existente incrementa por la ficha activa.
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Celdas número */
function NumberCell(props) {
  var cls = "rpos2-cell tone-" + (props.tone || "felt");
  if (props.tall) cls += " tall";
  if (props.amount > 0) cls += " selected";
  if (props.locked) cls += " locked";

  return (
    <button type="button" className={cls} onClick={props.onClick} disabled={props.locked}>
      <div className="rpos2-cell-top">
        <span className="rpos2-cell-label">{props.label}</span>
        {props.amount > 0 ? <span className="rpos2-betamt">{props.amount}</span> : null}
      </div>
    </button>
  );
}

/** Celdas "2 a 1" (solo visual por ahora) */
function SideCell(props) {
  return (
    <div className="rpos2-side">
      <span>{props.label}</span>
    </div>
  );
}
